import type { ReactNode } from 'react';
import { cn } from '@lib/utils/cn';
import { TONE_WASH, TONE_BAR } from '@components/ui/designTokens';

/**
 * Tarjeta KPI del dashboard rediseñado (Claude Design · Dashboard.dc.html).
 *
 * `footer` queda opcional a proposito: el diseño dibuja una mini-visualizacion
 * debajo de cada KPI, pero solo se llena en las tarjetas cuyo dato existe de
 * verdad en la API. Ver el comentario de omisiones en DashboardPage.tsx.
 */

export const DELTA_UP = 'text-[#0d9488]';
export const DELTA_DOWN = 'text-[#b8503a]';
export const DELTA_NEUTRAL = 'text-secondary-400';

export type DeltaType = 'positive' | 'negative' | 'neutral';

function DeltaArrow({ type }: { type: DeltaType }) {
  if (type === 'neutral') return null;
  // Triangulo a viewBox completo: el del diseño usaba un path de 24x24 que a
  // 10px se leia como un guion.
  return (
    <svg width="9" height="9" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
      {type === 'positive' ? <path d="M6 2l4.5 7.5h-9z" /> : <path d="M6 10L1.5 2.5h9z" />}
    </svg>
  );
}

interface DashboardKpiCardProps {
  index: string;
  icon: ReactNode;
  /**
   * Tono de la tarjeta: pinta el recuadro del icono, el lavado del fondo y la
   * franja de acento superior. Se elige por SIGNIFICADO del KPI (teal = dinero
   * y aprobadas, amber = pendientes, red = rechazadas, violet = clientes y
   * catalogo, neutral = totales), no por posicion.
   */
  iconTone?: 'neutral' | 'teal' | 'amber' | 'red' | 'violet';
  name: string;
  value: ReactNode;
  /** Opcional: si no hay un dato real de variación vs. período anterior, se omite en vez de mostrar un "N/A" que no dice nada. */
  change?: string;
  changeType?: DeltaType;
  description: string;
  footer?: ReactNode;
  'data-testid'?: string;
}

const ICON_TONES: Record<NonNullable<DashboardKpiCardProps['iconTone']>, string> = {
  neutral: 'bg-[#0a2233]/[.08] text-[#0a2233] dark:bg-white/10 dark:text-white',
  teal: 'bg-[#0d9488]/10 text-[#0d9488]',
  amber: 'bg-accent-500/10 text-accent-500',
  red: 'bg-[#b8503a]/10 text-[#b8503a]',
  violet: 'bg-[#6b239e]/10 text-[#6b239e]',
};

export function DashboardKpiCard({
  index,
  icon,
  iconTone = 'neutral',
  name,
  value,
  change,
  changeType,
  description,
  footer,
  'data-testid': testId,
}: DashboardKpiCardProps) {
  const deltaColor =
    changeType === 'positive' ? DELTA_UP : changeType === 'negative' ? DELTA_DOWN : DELTA_NEUTRAL;

  /**
   * Montos grandes ("B/. 9,986,997,048.17") no entraban en una tarjeta de
   * cuatro columnas a 26px y la tarjeta (overflow-hidden) los recortaba en
   * vez de mostrarlos completos. En vez de un tamaño fijo, se achica según
   * el largo del texto — los valores cortos (la mayoría) se ven igual que
   * siempre.
   */
  const valueLength = typeof value === 'string' ? value.length : 0;
  const valueSizeClass =
    valueLength > 18 ? 'text-[16px]' : valueLength > 14 ? 'text-[20px]' : 'text-[26px]';

  return (
    <div
      data-testid={testId}
      className={cn(
        'relative min-w-0 overflow-hidden rounded-[14px] border p-5 pt-[22px]',
        'shadow-[0_1px_2px_rgba(10,34,51,.03),0_8px_20px_-10px_rgba(10,34,51,.10)]',
        'transition-[border-color,box-shadow,transform] hover:-translate-y-px hover:shadow-[0_1px_2px_rgba(10,34,51,.04),0_14px_28px_-10px_rgba(10,34,51,.16)]',
        TONE_WASH[iconTone]
      )}
    >
      {/* Franja de acento superior: el identificador de color del KPI. */}
      <span aria-hidden="true" className={cn('absolute inset-x-0 top-0 h-[3px]', TONE_BAR[iconTone])} />
      <div className="flex items-center justify-between">
        <div className="inline-flex min-w-0 items-center gap-2">
          <span
            className={cn(
              'inline-flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-[7px]',
              ICON_TONES[iconTone]
            )}
          >
            {icon}
          </span>
          <span className="truncate text-[14px] font-semibold text-secondary-500 dark:text-secondary-400">
            {name}
          </span>
        </div>
        <span className="shrink-0 font-mono text-[11px] tracking-[0.08em] text-secondary-400">
          {index}
        </span>
      </div>

      {/*
        flex-wrap + 26px: los importes ("B/. 245,310.89") no entran a 32px en
        una tarjeta de cuatro columnas y partian el simbolo de moneda del
        numero. Si el valor es largo, el delta baja de linea en vez de romper
        la cifra.
      */}
      <div className="mt-3.5 flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span
          className={cn(
            'whitespace-nowrap font-bold leading-none tracking-[-0.03em] text-ink-850 tabular-nums dark:text-white',
            valueSizeClass
          )}
        >
          {value}
        </span>
        {change && (
          <span className={cn('inline-flex items-center gap-0.5 text-[13px] font-semibold', deltaColor)}>
            <DeltaArrow type={changeType ?? 'neutral'} />
            {change}
          </span>
        )}
      </div>
      <div className="mt-0.5 text-[12px] text-secondary-400">{description}</div>

      {footer && <div className="mt-3.5">{footer}</div>}
    </div>
  );
}
